import React, { useState, useEffect, useCallback } from 'react';
import { useToggle } from 'react-use';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';

import { Heading } from 'components/common/Elements/Elements';
import FeatureItems from './FeatureItems/FeatureItems';
import ConfirmationModal from '../parts/ConfirmationModal';
import { SWGControlConfirmModal } from '../parts/SWGControlConfirmModal';
import { modalActionBody } from 'components/common/Elements/NewModal/newModal';
import { postRequest } from 'utils/requests';
import { showNotif } from '@p81-common/ui';
import { access, resources, hasAccess } from 'utils/permissions';
import { defaultModalDetails } from '../helpers/config';
import { useContent } from 'contexts/content-context';

import { featuresFormater } from 'utils/featuresFormater';

import classes from './EnvironmentInfo.module.scss';

const EnvironmentInfo = ({
  environmentData: {
    customerId,
    featureAdoption,
    UCPData,
    idps,
    idpMfa,
    swgControlledBy
  }
}) => {
  const { t } = useTranslation(['activity']);
  const { url } = useRouteMatch();
  const newUrl = url.split('/').slice(0, -1).join('/');

  const data = featureAdoption;
  const ucpData = UCPData;

  const [swgControl, setSwgControl] = useState(
    data?.swgControlledBy || swgControlledBy
  );

  const [swgConfirmOpen, toggleSwgConfirmOpen] = useToggle(false);
  const {
    contentState: { customerData }
  } = useContent();

  const handleSwgControlSwitch = async () => {
    try {
      const tenantUUID = customerData?.tenantUUID;
      if (tenantUUID) {
        const apiRes = await postRequest(
          '/customer/platform/security/swg/control',
          {
            controlledBy: 'hsase',
            customerId,
            tenantUUID
          }
        );
        if (apiRes?.statusCode === 200) {
          showNotif('Internet Access control set to Check Point SASE', {
            alertProps: { variant: 'filled', severity: 'success' }
          });
          setSwgControl('hsase');
        }
      }
    } catch (e) {
      showNotif('Failed to update Internet Access control', {
        alertProps: { variant: 'filled', severity: 'error' }
      });
    }
  };

  const openSwgControlModal = () => {
    toggleSwgConfirmOpen(true);
  };

  const [modalOpen, toggleModalOpen] = useToggle(false);
  const [modalDetails, setModalDetails] = useState(defaultModalDetails);
  const [idpMfaState, setIdpMfaState] = useState(idpMfa);

  const handleCloseModal = () => {
    toggleModalOpen(false);
    setModalDetails(defaultModalDetails);
  };

  const handleOpenModal = (featureName, featureCurValue) => () => {
    setModalDetails({
      newStatus: featureName,
      featureName: !featureCurValue ? 'On' : 'Off'
    });
    toggleModalOpen();
  };

  const onFeatureUpdate = (featureName, featureCurValue) => async () => {
    const apiRes = await postRequest('/customer/environment/feature_update', {
      customerId: customerId,
      featureName: featureName,
      enabled: !featureCurValue
    });
    if (apiRes?.statusCode === 200) {
      data[featureName] = !featureCurValue;
      setIdentityProviders(getIdentityProvidersData(data, idps));
    }
    handleCloseModal();
  };

  const IDP_TYPES = {
    DATABASE: 'database',
    AZURE: 'azureAD',
    G_SUITE: 'gsuite',
    LDAP: 'adLdap',
    OKTA: 'okta'
  };

  const removeMfa = (idpType, idp) => {
    const idpTypeKey = Object.keys(IDP_TYPES).find(
      key => IDP_TYPES[key] === idpType
    );
    const idpTypeTitle = t(`activity:customers.${idpTypeKey}`);

    modalActionBody({
      title: t('activity:modalText.REMOVE_IDP_2FA_TITLE'),
      callBack: () => handleRemoveMfa(idpType, idp),
      text: t('activity:modalText.REMOVE_IDP_2FA_CONFIRM_TEXT', {
        idpType: idpTypeTitle
      }),
      okText: t('activity:common.REMOVE'),
      cancelText: t('activity:featureFlag.NO_THANKS')
    });
  };

  const handleRemoveMfa = async (idpType, idp) => {
    try {
      const promises =
        Array.isArray(idp) && idp.length
          ? idp.map(el =>
              postRequest('/customer/environment/remove-mfa', {
                customerId: customerId,
                idpType: idpType,
                connectionId: el.id
              })
            )
          : [
              postRequest('/customer/environment/remove-mfa', {
                customerId: customerId,
                idpType: idpType,
                connectionId: idp.connectionId
              })
            ];

      const results = await Promise.all(promises);
      showNotif(results[0]?.message, {
        alertProps: {
          variant: 'filled',
          severity: 'success'
        }
      });
      setIdpMfaState({ ...idpMfaState, [idpType]: false });
    } catch (e) {
      showNotif(e?.response?.data?.message, {
        alertProps: {
          variant: 'filled',
          severity: 'error'
        }
      });
    }
  };

  const hasCSCustomerEnvFeatureWriteAccess = hasAccess([
    resources.CS_CUSTOMER_ENV_FEATURE_ENABLE,
    access.WRITE
  ]);

  const getIdentityProvidersData = useCallback(
    (data, idps) => {
      return [
        {
          title: t('activity:customers.G_SUITE'),
          value: data.gsuiteEnabled,
          subtable: [
            {
              title: `Scim`,
              value: data.gsuiteScimEnabled
            },
            {
              title: `2FA`,
              value: idpMfaState?.[IDP_TYPES.G_SUITE],
              hideNext:
                !idpMfaState?.[IDP_TYPES.G_SUITE] ||
                !data.gsuiteAuthenticationRequestsDuration ||
                !data.gsuiteAuthenticationRequests,
              isActButtonVisible:
                idpMfaState?.[IDP_TYPES.G_SUITE] &&
                hasCSCustomerEnvFeatureWriteAccess,
              onClick: () => removeMfa(IDP_TYPES.G_SUITE, idps.gsuite),
              buttonTitle: t('activity:common.REMOVE')
            },

            {
              title:
                typeof data.gsuiteAuthenticationRequests == 'string'
                  ? t(`activity:customers.${data.gsuiteAuthenticationRequests}`)
                  : t('activity:customers.NO_AUTHENTICATION_METHOD'),
              value:
                typeof data.gsuiteAuthenticationRequestsDuration == 'string'
                  ? t(
                      `activity:customers.${data.gsuiteAuthenticationRequestsDuration}`
                    )
                  : data.gsuiteAuthenticationRequestsDuration
            }
          ]
        },
        {
          title: t('activity:customers.AZURE'),
          value: data.azureEnabled,
          subtable: [
            {
              title: `Scim`,
              value: data.azureScimEnabled
            },
            {
              title: `2FA`,
              value: idpMfaState?.[IDP_TYPES.AZURE],
              hideNext:
                !idpMfaState?.[IDP_TYPES.AZURE] ||
                !data.azureAuthenticationRequests ||
                !data.azureAuthenticationRequestsDuration,
              isActButtonVisible:
                idpMfaState?.[IDP_TYPES.AZURE] &&
                hasCSCustomerEnvFeatureWriteAccess,
              onClick: () => removeMfa(IDP_TYPES.AZURE, idps.azureAD),
              buttonTitle: t('activity:common.REMOVE')
            },

            {
              title:
                typeof data.azureAuthenticationRequests == 'string'
                  ? t(`activity:customers.${data.azureAuthenticationRequests}`)
                  : t('activity:customers.NO_AUTHENTICATION_METHOD'),
              value:
                typeof data.azureAuthenticationRequestsDuration == 'string'
                  ? t(
                      `activity:customers.${data.azureAuthenticationRequestsDuration}`
                    )
                  : data.azureAuthenticationRequestsDuration
            }
          ]
        },
        {
          title: t('activity:customers.OKTA'),
          value: data.oktaEnabled,
          subtable: [
            {
              title: `Scim`,
              value: data.oktaScimEnabled
            },
            {
              title: `2FA`,
              value: idpMfaState?.[IDP_TYPES.OKTA],
              hideNext:
                !idpMfaState?.[IDP_TYPES.OKTA] ||
                !data.oktaAuthenticationRequests ||
                !data.oktaAuthenticationRequestsDuration,
              isActButtonVisible:
                idpMfaState?.[IDP_TYPES.OKTA] &&
                hasCSCustomerEnvFeatureWriteAccess,
              onClick: () => removeMfa(IDP_TYPES.OKTA, idps.okta),
              buttonTitle: t('activity:common.REMOVE')
            },
            {
              title:
                typeof data.oktaAuthenticationRequests == 'string'
                  ? t(`activity:customers.${data.oktaAuthenticationRequests}`)
                  : t('activity:customers.NO_AUTHENTICATION_METHOD'),
              value:
                typeof data.oktaAuthenticationRequestsDuration == 'string'
                  ? t(
                      `activity:customers.${data.oktaAuthenticationRequestsDuration}`
                    )
                  : data.oktaAuthenticationRequestsDuration
            }
          ]
        },
        {
          title: t('activity:customers.LDAP'),
          value: data.adldapEnabled,
          subtable: [
            {
              title: `2FA`,
              value: idpMfaState?.[IDP_TYPES.LDAP],
              isActButtonVisible:
                idpMfaState?.[IDP_TYPES.LDAP] &&
                hasCSCustomerEnvFeatureWriteAccess,
              onClick: () => removeMfa(IDP_TYPES.LDAP, idps.adLdap),
              buttonTitle: t('activity:common.REMOVE')
            }
          ]
        },
        {
          title: t('activity:customers.DATABASE'),
          value: data.databaseEnabled,
          isActButtonVisible:
            !data.databaseEnabled && hasCSCustomerEnvFeatureWriteAccess,
          onChange: handleOpenModal(
            t('activity:customers.DATABASE'),
            data.databaseEnabled
          ),
          subtable: [
            {
              title: `2FA`,
              value: idpMfaState?.[IDP_TYPES.DATABASE],
              isActButtonVisible:
                idpMfaState?.[IDP_TYPES.DATABASE] &&
                hasCSCustomerEnvFeatureWriteAccess,
              onClick: () => removeMfa(IDP_TYPES.DATABASE, idps.database),
              buttonTitle: t('activity:common.REMOVE')
            }
          ]
        }
      ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idpMfaState]
  );

  const [identityProviders, setIdentityProviders] = useState(
    getIdentityProvidersData(data, idps)
  );

  useEffect(() => {
    setIdentityProviders(getIdentityProvidersData(data, idps));
  }, [idpMfaState, data, idps, getIdentityProvidersData]);

  const [optionsClient, setOptionsClient] = useState([]);
  const [valueOptions, setValueOptions] = useState('');
  const [clients, setClients] = useState([]);

  useEffect(() => {
    const optionsClient = ucpData.map(ucp => {
      return { value: ucp.name, label: ucp.name };
    });

    setOptionsClient(optionsClient);
    setValueOptions(optionsClient[0] ? optionsClient[0].label : '');
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const changeUcp = ucpData.find(ucp => ucp.name === valueOptions);

    if (changeUcp) {
      const arrClients = [
        {
          title: featuresFormater('allowAccessPublicLocation', t),
          value: changeUcp.allowAccessPublicLocation
        },
        {
          title: featuresFormater('connectOnLaunch', t),
          value: changeUcp.connectOnLaunch
        },
        {
          title: featuresFormater('connectDisconnectNotification', t),
          value: changeUcp.connectDisconnectNotification
        },
        {
          title: featuresFormater('forceUpdate', t),
          value: changeUcp.forceUpdate
        },
        {
          title: featuresFormater('crashReports', t),
          value: changeUcp.crashReports
        },
        {
          title: featuresFormater('snowplowReport', t),
          value: changeUcp.snowplowReport
        },
        {
          title: featuresFormater('autoWifisec', t),
          value: changeUcp.autoWifisec
        },
        {
          title: featuresFormater('vpnAlwaysOn', t),
          value: changeUcp.vpnAlwaysOn
        },
        {
          title: featuresFormater('alwaysOnEnabled', t),
          value: changeUcp.alwaysOnEnabled
        },
        {
          title: featuresFormater('killSwitch', t),
          value: changeUcp.killSwitch
        },
        {
          title: featuresFormater('trustedWifiNetworks', t),
          value: changeUcp.trustedWifiNetworks
        },
        {
          title: featuresFormater('autoLogout', t),
          value: changeUcp.autoLogout
        },
        {
          title: featuresFormater('startMinimized', t),
          value: changeUcp.startMinimized
        },
        {
          title: featuresFormater('publicVpnNetworks', t),
          value: changeUcp.publicVpnNetworks
        },
        {
          title: 'Windows',
          value:
            changeUcp.windowsProtocol === 'wireguard' ||
            changeUcp.windowsProtocol === 'ikev2' ||
            changeUcp.windowsProtocol === 'openVpn' ||
            changeUcp.windowsSilentUpdate ||
            changeUcp.windowsDnsLeak ||
            changeUcp.windowsNotifyReconnect,
          subtable: [
            {
              title: `Default Protocol - WireGuard`,
              value: changeUcp.windowsProtocol === 'wireguard'
            },
            {
              title: `Default Protocol - IKEv2`,
              value: changeUcp.windowsProtocol === 'ikev2'
            },
            {
              title: `Default Protocol - OpenVPN`,
              value: changeUcp.windowsProtocol === 'openVpn'
            },
            {
              title: featuresFormater('automaticSilentUpdate', t),
              value: changeUcp.windowsSilentUpdate
            },
            {
              title: featuresFormater('disableUpdate', t),
              value: changeUcp.isWinDisabledUpdates
            },
            {
              title: featuresFormater('dnsLeak', t),
              value: changeUcp.windowsDnsLeak
            },
            {
              title: featuresFormater('notifyReconnect', t),
              value: changeUcp.windowsNotifyReconnect
            }
          ]
        },
        {
          title: 'Android',
          value:
            changeUcp.androidProtocol === 'wireguard' ||
            changeUcp.androidProtocol === 'ikev2' ||
            changeUcp.androidProtocol === 'openVpn',
          subtable: [
            {
              title: `Default Protocol - WireGuard`,
              value: changeUcp.androidProtocol === 'wireguard'
            },
            {
              title: `Default Protocol - IKEv2`,
              value: changeUcp.androidProtocol === 'ikev2'
            },
            {
              title: `Default Protocol - OpenVPN`,
              value: changeUcp.androidProtocol === 'openVpn'
            }
          ]
        },
        {
          title: 'Mac',
          value:
            changeUcp.macProtocol === 'wireguard' ||
            changeUcp.macProtocol === 'ikev2' ||
            changeUcp.macProtocol === 'openVpn' ||
            changeUcp.macDnsLeak ||
            changeUcp.macSilentUpdate,
          subtable: [
            {
              title: `Default Protocol - WireGuard`,
              value: changeUcp.macProtocol === 'wireguard'
            },
            {
              title: `Default Protocol - IKEv2`,
              value: changeUcp.macProtocol === 'ikev2'
            },
            {
              title: `Default Protocol - OpenVPN`,
              value: changeUcp.macProtocol === 'openVpn'
            },
            {
              title: featuresFormater('automaticSilentUpdate', t),
              value: changeUcp.macSilentUpdate
            },
            {
              title: featuresFormater('disableUpdate', t),
              value: changeUcp.isMacDisabledUpdates
            },
            {
              title: featuresFormater('dnsLeak', t),
              value: changeUcp.macDnsLeak
            }
          ]
        },
        {
          title: 'Linux',
          value:
            changeUcp.linuxProtocol === 'wireguard' ||
            changeUcp.linuxProtocol === 'ikev2' ||
            changeUcp.linuxProtocol === 'openVpn' ||
            changeUcp.linuxSilentUpdate,
          subtable: [
            {
              title: `Default Protocol - WireGuard`,
              value: changeUcp.linuxProtocol === 'wireguard'
            },
            {
              title: `Default Protocol - IKEv2`,
              value: changeUcp.linuxProtocol === 'ikev2'
            },
            {
              title: `Default Protocol - OpenVPN`,
              value: changeUcp.linuxProtocol === 'openVpn'
            },
            {
              title: featuresFormater('automaticSilentUpdate', t, true),
              value: changeUcp.linuxSilentUpdate
            },
            {
              title: featuresFormater('disableUpdate', t),
              value: changeUcp.isLinuxDisabledUpdates
            }
          ]
        },
        {
          title: 'iOS',
          value: changeUcp.iosAutoreconnect,
          subtable: [
            {
              title: `Auto reconnect`,
              value: changeUcp.iosAutoreconnect
            }
          ]
        }
      ];

      setClients(arrClients);
    }
    // eslint-disable-next-line
  }, [valueOptions]);

  const onChangeOptions = e => {
    setValueOptions(e);
  };

  const networkFeatures = [
    {
      title: t('activity:platform.TUNNELS'),
      value: data.isUsingTunnels
    },
    {
      title: t('activity:platform.SPLIT_TUNNELING'),
      value: data.splitTunnel
    },
    {
      title: t('activity:platform.CUSTOM_DNS'),
      value: data.customDnsEnabled
    }
  ];

  // Compute Web Security title with optional control label
  const webSecurityTitle =
    data?.swgControlledBy === 'quantum' || swgControl === 'quantum'
      ? `${t(
          'activity:platform.WEB_SECURITY'
        )} (Controlled by Check Point Firewall)`
      : t('activity:platform.WEB_SECURITY');

  const securityFeatures = [
    {
      title: t('activity:platform.DNS_FILTERING'),
      value: data.usingDnsfiltering
    },
    {
      title: t('activity:about.FIREWALL'),
      value: data.isNprUsing
    },
    {
      title: 'Device Posture Check',
      value: data.isDpcUsing,
      link: data.isDpcUsing ? `${newUrl}/platform/dpc` : undefined,
      date: data.swgFirstEnabledDate
    },
    {
      title: webSecurityTitle,
      value: data.isUsingSwg || data.isUsingBypass,
      // Show action only when currently controlled by quantum
      topActionTitle:
        data?.swgControlledBy === 'quantum' || swgControl === 'quantum'
          ? 'Set HSASE'
          : undefined,
      topActionOnClick:
        data?.swgControlledBy === 'quantum' || swgControl === 'quantum'
          ? openSwgControlModal
          : undefined,
      subtable: [
        {
          title: t('activity:platform.WEB_FILTERING_RULES'),
          value: data.isUsingSwg,
          link: data.isUsingSwg ? `${newUrl}/platform/swg` : undefined,
          date: data.swgFirstEnabledDate
        },
        {
          title: t('activity:platform.BYPASS_RULES'),
          value: data.isUsingBypass,
          link: data.isUsingBypass ? `${newUrl}/platform/swgBp` : undefined,
          date: data.bypassFirstEnabledDate
        }
      ]
    }
  ];

  const dashboard = [
    {
      title: t('activity:customers.MONITORING_DASHBOARD'),
      value: data.isUsingMonitoringDashboard,
      date: data.firstUsingMonitoringDate
    }
  ];

  const sieMIntegration = [
    {
      title: t('activity:customers.SPLUNK'),
      value: data.splunk
    },
    {
      title: t('activity:customers.AMAZON_S3'),
      value: data.amazons3
    },
    {
      title: t('activity:customers.AZURE_SENTINEL'),
      value: data.azureSentinel
    }
  ];

  const application = [
    {
      title: t('activity:features.HTTP'),
      value: data.http
    },
    {
      title: t('activity:features.HTTPS'),
      value: data.https
    },
    {
      title: t('activity:features.RDP'),
      value: data.rdp
    },
    {
      title: t('activity:features.SSH'),
      value: data.ssh
    },
    {
      title: t('activity:features.VNC'),
      value: data.vnc
    },
    {
      title: t('activity:platform.POLICIES'),
      value: data.policies
    },
    {
      title: t('activity:platform.EXTERNAL_API'),
      value: data.isUsingExternalApi
    }
  ];

  return (
    <>
      <section className={classes.box}>
        <Heading className={classes.title}>
          {t('activity:customers.FEATURE_ADOPTION')}
        </Heading>
        <FeatureItems
          data={identityProviders}
          title={t('activity:customers.IDENTITY_PROVIDERS')}
        />
        <FeatureItems
          data={securityFeatures}
          title={t('activity:customers.SECURITY_FEATURES')}
        />
        <FeatureItems
          data={networkFeatures}
          title={t('activity:customers.NETWORK_FEATURES')}
        />
        {valueOptions && (
          <FeatureItems
            data={clients}
            options={optionsClient}
            valueOptions={valueOptions}
            onChangeOptions={onChangeOptions}
            title={t('activity:customers.AGENT_AND_PLATFORM_CONFIG')}
          />
        )}

        <FeatureItems
          data={application}
          title={t('activity:platform.APPLICATIONS')}
        />
        <FeatureItems
          data={dashboard}
          title={t('activity:customers.DASHBOARDS')}
        />
        <FeatureItems
          data={sieMIntegration}
          title={t('activity:customers.SIEM_INTEGRATION')}
        />
      </section>
      <ConfirmationModal
        modalDetails={modalDetails}
        open={modalOpen}
        handleConfirmClick={onFeatureUpdate(
          'databaseEnabled',
          data.databaseEnabled
        )}
        handleCloseClick={handleCloseModal}
      />
      <SWGControlConfirmModal
        open={swgConfirmOpen}
        onConfirm={async () => {
          await handleSwgControlSwitch();
          toggleSwgConfirmOpen(false);
        }}
        onClose={() => toggleSwgConfirmOpen(false)}
      />
    </>
  );
};

EnvironmentInfo.propTypes = {
  environmentData: PropTypes.object
};

export default EnvironmentInfo;
